import { Num } from '../utils';
import { Circle, Point, Vector, Intersections, IPhysicalObject, PhysicalObject } from '.';
export class Segment extends PhysicalObject implements IPhysicalObject {
public vecx: number;
public vecy: number;
/**
 * @param {?} x
 * @param {?} y
 * @param {?} vecx
 * @param {?} vecy
 */
constructor(x: number, y: number, vecx: number, vecy: number) {
    super();
    this.x = x;
    this.y = y;
    this.vecx = vecx;
    this.vecy = vecy;
  }
/**
 * @return {?}
 */
public length(): number {
    let /** @type {?} */ dx = Math.pow(this.vecx, 2);
    let /** @type {?} */ dy = Math.pow(this.vecy, 2);
    return Math.sqrt(dx + dy);
  }
/**
 * @return {?}
 */
public normal(): Segment {
    let /** @type {?} */ x1 = this.y;
    let /** @type {?} */ y1 = this.vecx + this.x;
    let /** @type {?} */ y2 = this.x;
    let /** @type {?} */ x2 = this.vecy + this.y;
    return new Segment(x1, y1, x2-x1, y2-y1);
  }
/**
 * @return {?}
 */
public center(): Point {
    let /** @type {?} */ x = (this.x * 2 + this.vecx) / 2;
    let /** @type {?} */ y = (this.y * 2 + this.vecy) / 2;
    return new Point(x, y);
  }
/**
 * @return {?}
 */
public unit(): Segment {
    return new Segment(0, 0, this.vecx / this.length(), this.vecy / this.length());
  }
/**
 * @param {?} multiplier
 * @return {?}
 */
public multiply(multiplier: number): void { 
    this.vecx *= multiplier;
    this.vecy *= multiplier;
  }
/**
 * @param {?} segmentOnto
 * @return {?}
 */
public project(segmentOnto: Segment): Point {
    let /** @type {?} */ vector = new Vector(this.vecx, this.vecy);
    let /** @type {?} */ onto = new Vector(segmentOnto.vecx, segmentOnto.vecy);
    
    // dot product to return a value
    let /** @type {?} */ d = onto.dot(onto);

    if(d > 0) {
      let /** @type {?} */ dproj = vector.dot(onto);
      let /** @type {?} */ multiplier = dproj / d;
      let /** @type {?} */ rx = onto.x * multiplier;
      let /** @type {?} */ ry = onto.y * multiplier;
      return new Point(rx, ry);
    }

    return new Point(0, 0);
  }
/**
 * @return {?}
 */
public slope(): number {
    return (this.vecy - this.y) / (this.vecx - this.x);
  }
/**
 * @param {?} point
 * @return {?}
 */
public pointInside(point: Point): boolean {
    let /** @type {?} */ circle = new Circle(point.x, point.y, 1);
    return circle.intersect(this);
  }
/**
 * @param {?} shape
 * @return {?}
 */
public intersect(shape: IPhysicalObject): boolean {
    return this.intersectSegment( /** @type {?} */((<Segment>shape))) != null;
  }
/**
 * @param {?} segment
 * @return {?}
 */
public intersectSegment(segment: Segment): Point {
    
    // line a
    let /** @type {?} */ x1 = this.x;
    let /** @type {?} */ y1 = this.y;
    let /** @type {?} */ x2 = this.x + this.vecx;
    let /** @type {?} */ y2 = this.y + this.vecy;

    // line b
    let /** @type {?} */ x3 = segment.x;
    let /** @type {?} */ y3 = segment.y;
    let /** @type {?} */ x4 = segment.x + segment.vecx;
    let /** @type {?} */ y4 = segment.y + segment.vecy;

    let /** @type {?} */ a1, /** @type {?} */ a2, /** @type {?} */ b1, /** @type {?} */ b2, /** @type {?} */ c1, /** @type {?} */ c2;
    let /** @type {?} */ r1, /** @type {?} */ r2, /** @type {?} */ r3, /** @type {?} */ r4;
    let /** @type {?} */ denom, /** @type {?} */ offset, /** @type {?} */ num;

    a1 = y2 - y1;
    b1 = x1 - x2;
    c1 = (x2 * y1) - (x1 * y2);

    r3 = ((a1 * x3) + (b1 * y3) + c1);
    r4 = ((a1 * x4) + (b1 * y4) + c1);

    if((r3 != 0) && (r4 != 0) && Num.sameSign(r3, r4))
      return null;

    a2 = y4 - y3;
    b2 = x3 - x4;
    c2 = (x4 * y3) - (x3 * y4);
    r1 = (a2 * x1) + (b2 * y1) + c2;
    r2 = (a2 * x2) + (b2 * y2) + c2;

    if((r1 != 0) && (r2 != 0) && Num.sameSign(r1, r2))
      return null;

    // Line segments intersect. Find the intersection point
    denom = (a1 * b2) - (a2 * b1);

    if(denom == 0)
      return null;

    if(denom < 0) {
      offset = -denom / 2;
    } else {
      offset = denom / 2;
    }

    let /** @type {?} */ intx, /** @type {?} */ inty;
    num = (b1 * c2) - (b2 * c1);
    if(num < 0) {
      intx = (num - offset) / denom;
    } else {
      intx = (num + offset) / denom;
    }

    num = (a2 * c1) - (a1 * c2);
    if(num < 0) {
      inty = (num - offset) / denom;
    } else {
      inty = (num + offset) / denom;
    }

    return new Point(intx, inty);
  }
}

function Segment_tsickle_Closure_declarations() {
/** @type {?} */
Segment.prototype.vecx;
/** @type {?} */
Segment.prototype.vecy;
}
